import { ExceptionHandlingPolicy, IConfigurationSend, Task } from '../components/Home/ISettings';
import { config } from '../config';
import { useAppSelector } from '../redux/store';
import { UserRoleEnums } from '../redux/store/IStore';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

const usePipeline = () => {
  const { enqueueSnackbar } = useSnackbar();

  const activeConfiguration = useAppSelector((store) => store.app.activeConfiguration);
  const { replacementSets } = useAppSelector((store) => store.app.replacements);
  const { boostSets } = useAppSelector((store) => store.app.boosts);
  const activePunctuator = useAppSelector((store) => store.app.activePunctuator);
  const activeInverseTextNormalizator = useAppSelector((store) => store.app.activeInverseTextNormalizator);
  const activeTrueCaser = useAppSelector((store) => store.app.activeTrueCaser);
  const speakerSettingsModal = useAppSelector((store) => store.app.speakerSettingsModal);
  const user = useAppSelector((store) => store.app.user);
  const isUserFrameworkChanger = user && user.userRoles.includes(UserRoleEnums.FRAMEWORK_CHANGER);

  const configuration = useAppSelector((store) => store.app.configuration);

  const buildPipeline = useCallback((): IConfigurationSend | null => {
    if (
      !activeConfiguration.model ||
      !activeConfiguration.language ||
      !activeConfiguration.domain ||
      !activeConfiguration.model
    ) {
      enqueueSnackbar(`Operacija ni možna, ker ni izbran noben model. Preverite nastavitve.`, {
        variant: 'error',
        autoHideDuration: 4000,
      });
      return null;
    }

    const liveConfig: IConfigurationSend = [];

    let asrParameters = {};
    if (activeConfiguration.model.boostedPhraseSets) {
      asrParameters['boostedPhraseSets'] = boostSets
        ? boostSets.filter((boostSet) => boostSet.checked).map((boostSet) => boostSet.set.id)
        : undefined;
    }
    if (activeConfiguration.model.enableUnks && activeConfiguration.model.enableUnks.value)
      asrParameters['enableUnks'] = true;
    if (
      activeConfiguration.model.enableInterims &&
      activeConfiguration.model.enableInterims.value &&
      !config.frameworksIgnoringIterims.includes(activeConfiguration.serviceProvider ?? '')
    )
      asrParameters['enableInterims'] = true;
    if (activeConfiguration.model.enableSd && activeConfiguration.model.enableSd.value) {
      asrParameters['enableSd'] = true;
      if (speakerSettingsModal.numberOfSpeakers) {
        asrParameters['sdMinSpeakers'] = speakerSettingsModal.numberOfSpeakers;
        asrParameters['sdMaxSpeakers'] = speakerSettingsModal.numberOfSpeakers;
      } else {
        if (speakerSettingsModal.minNumberOfSpeakers)
          asrParameters['sdMinSpeakers'] = speakerSettingsModal.minNumberOfSpeakers;
        if (speakerSettingsModal.maxNumberOfSpeakers)
          asrParameters['sdMaxSpeakers'] = speakerSettingsModal.maxNumberOfSpeakers;
      }
      if (activeConfiguration.model.sdSpeakerIdEnabled && activeConfiguration.model.sdSpeakerIdEnabled.value) {
        asrParameters['sdSpeakerIdEnabled'] = true;
      }
    }

    let activeFramework = activeConfiguration.serviceProvider;
    if (!isUserFrameworkChanger || !activeFramework) {
      const frameworks = Object.keys(configuration);
      frameworks.forEach((framework) => {
        if (
          configuration[framework] &&
          configuration[framework][activeConfiguration.language!] &&
          configuration[framework][activeConfiguration.language!][activeConfiguration.domain!] &&
          configuration[framework][activeConfiguration.language!][activeConfiguration.domain!][
            activeConfiguration.model!.name
          ]
        ) {
          activeFramework = framework;
        }
      });
    }

    liveConfig.push({
      task: Task.asr,
      config: {
        tag: `${activeFramework}:${activeConfiguration.language}:${activeConfiguration.domain}:${activeConfiguration.model.name}`,
        parameters: asrParameters,
      },
      exceptionHandlingPolicy: ExceptionHandlingPolicy.THROW,
    });

    if (
      activePunctuator.toggled &&
      !(activePunctuator.language === null || activePunctuator.domain === null || activePunctuator.model === null) &&
      !activeConfiguration.model.autoPc
    ) {
      //TODO: Add service provider to punctuator
      //TODO: Fix this.
      let pcParameters = {};
      if (activePunctuator.model.enableSplitToSentences && activePunctuator.model.enableSplitToSentences.value) {
        pcParameters['enableSplitToSentences'] = true;
      }
      if (
        activePunctuator.model.enableTc &&
        activePunctuator.model.enableTc.value &&
        activeTrueCaser.language === null
      ) {
        pcParameters['enableTc'] = true;
      }
      liveConfig.push({
        task: Task.pc,
        config: {
          tag: `${activePunctuator.model.serviceProvider}:${activePunctuator.language}:${activePunctuator.domain}:${activePunctuator.model.name}`,
          parameters: pcParameters,
        },
        exceptionHandlingPolicy: ExceptionHandlingPolicy.SKIP,
      });
    }

    if (
      activeInverseTextNormalizator.toggled &&
      !(
        activeInverseTextNormalizator.language === null ||
        activeInverseTextNormalizator.domain === null ||
        activeInverseTextNormalizator.model === null
      )
    ) {
      //TODO: Add service provider to normalizer
      liveConfig.push({
        task: Task.itn,
        config: {
          tag: `${activeInverseTextNormalizator.model.serviceProvider}:${activeInverseTextNormalizator.language}:${activeInverseTextNormalizator.domain}:${activeInverseTextNormalizator.model.name}`,
          parameters: {},
        },
        exceptionHandlingPolicy: ExceptionHandlingPolicy.SKIP,
      });
    }

    console.log('-------------------');
    console.log(activeTrueCaser);
    console.log('-------------------');
    if (
      activeTrueCaser.toggled &&
      !(activeTrueCaser.language === null || activeTrueCaser.domain === null || activeTrueCaser.model === null) &&
      !activeConfiguration.model.autoTc
    ) {
      //TODO: Add service provider to trueCaser
      liveConfig.push({
        task: Task.tc,
        config: {
          tag: `${activeTrueCaser.model.serviceProvider}:${activeTrueCaser.language}:${activeTrueCaser.domain}:${activeTrueCaser.model.name}`,
          parameters: {},
        },
        exceptionHandlingPolicy: ExceptionHandlingPolicy.SKIP,
      });
    }

    //TODO: Update to not be hardcoded. Good for now.

    let replacementSetsIds: number[] = [];
    if (replacementSets) {
      replacementSetsIds = replacementSets.filter((set) => set.checked).map((set) => set.set.id);

      const globalPunctuationSet = replacementSets.filter((set) => set.set.name === 'dict.pnc');
      if (globalPunctuationSet.length > 0 && activeConfiguration.model.dictatedPunctuations?.value) {
        replacementSetsIds.push(globalPunctuationSet[0].set.id);
      }

      const globalCommandSet = replacementSets.filter((set) => set.set.name === 'dict.cmd');
      if (globalCommandSet.length > 0 && activeConfiguration.model.dictatedCommands?.value) {
        replacementSetsIds.push(globalCommandSet[0].set.id);
      }
    }

    liveConfig.push({
      task: Task.ac,
      config: {
        tag: `${'LOCAL'}:${'*'}:${'*'}:${'*'}`,
        parameters: {
          replacementSets: replacementSetsIds,
        },
      },
      exceptionHandlingPolicy: ExceptionHandlingPolicy.SKIP,
    });

    return liveConfig;
  }, [
    configuration,
    activeConfiguration,
    activePunctuator,
    activeInverseTextNormalizator,
    activeTrueCaser,
    speakerSettingsModal,
    replacementSets,
    boostSets,
  ]);

  return {
    buildPipeline,
  };
};

export default usePipeline;
